import { readUserMessages, isAssistantText } from '../utils/user-messages';
import {
  isValidElement,
  useCallback,
  useMemo,
  useState,
  type DragEvent,
  type Ref,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chat,
  ChatInput,
  ChatMessage,
  cn,
  EMessageDirection,
  EMessageStatus,
  EMessageType,
  Flex,
  toast,
  type IChatInputChip,
  type IChatMessage,
  type IChatThinkingTexts,
  type IconType,
} from '@kognitos/lattice';

import { usePersistedDraftPrompt } from '@/shared/hooks/use-persisted-draft-prompt';
import {
  foldSelectionSnippets,
  useSelectionContext,
} from '@/shared/hooks/use-selection-context';
import { useThinkingTexts } from '@/shared/hooks/useThinkingTexts';
import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { downloadOutput } from '@/modules/outputs/utils/download';

import type { IUseRainThreadMessagesReturn } from '../hooks/types';
import type { IUseRainChatAttachmentsReturn } from '../hooks/useRainChatAttachments';
import type { IUseRainArtifactPreviewReturn } from '../hooks/useRainArtifactPreview';
import { useSustainedFlag } from '../hooks/useSustainedFlag';
import { FOLLOW_UP_ID_PREFIX, TRANSFER_MARKER_ID_PREFIX } from '../constants';
import { foldWorkRuns } from '../utils/work-block';
import { isFullWidthCardElement } from './full-width-card';
import { RainToolCallsToggle } from './RainToolCallsToggle';
import {
  RainToolCallCard,
  type IRainToolCallCardProps,
} from './RainToolCallCard';
import {
  RainFollowUpCard,
  type IRainFollowUpCardProps,
} from './RainFollowUpCard';

/**
 * A message whose widget is the GENERIC tool-call card.
 *
 * The same identification `renderMessage` makes below, and it works for the
 * same reason: the generic card lives in the core beside this view, so it can
 * be named here. An agent's own card cannot be — profiles depend on the core,
 * never the reverse — which is precisely what makes this a usable boundary
 * between "tool trivia" and "specially handled".
 */
const isGenericToolCardMessage = (message: IChatMessage): boolean =>
  isValidElement(message.widget?.customContent) &&
  message.widget.customContent.type === RainToolCallCard;

/**
 * Agent text written between tool calls: narration of work in progress, not a
 * reply. Any agent text followed by a card this toggle HIDES, before the user
 * speaks again. A card that stays on screen is not that boundary — a follow-up
 * chip row, a sleep card or a connection picker sits after the reply it belongs
 * to, and reading them as tool calls took the reply down with them.
 * The last text of a finished turn is the reply; while the agent is still
 * working, that trailing text may be narration too, so it waits. Follow-up
 * suggestions come after the reply, so they don't make it narration.
 */
function interimNarrationIds(
  messages: IChatMessage[],
  working: boolean,
): Set<string> {
  const ids = new Set<string>();
  let pending: string[] = [];
  for (const message of messages) {
    if (message.direction === EMessageDirection.OUTGOING) pending = [];
    else if (message.widget) {
      if (message.id.startsWith(FOLLOW_UP_ID_PREFIX)) continue;
      if (
        isGenericToolCardMessage(message) &&
        !isAwaitingApprovalMessage(message)
      )
        pending.forEach(id => ids.add(id));
      pending = [];
    } else if (message.content?.trim() && message.type !== 'complete')
      pending.push(message.id);
  }
  if (working) pending.forEach(id => ids.add(id));
  return ids;
}

/**
 * Each follow-up card's chips run through `filter` with the user's last
 * message and the agent's last reply before it; a card left empty goes.
 */
function trimFollowUps(
  messages: IChatMessage[],
  filter: NonNullable<IRainChatViewProps['filterFollowUps']>,
): IChatMessage[] {
  let lastUser = '';
  let lastReply = '';
  return messages.flatMap(message => {
    const card = message.widget?.customContent;
    if (
      message.id.startsWith(FOLLOW_UP_ID_PREFIX) &&
      isValidElement<IRainFollowUpCardProps>(card) &&
      card.type === RainFollowUpCard
    ) {
      const kept = filter(card.props.suggestions, { lastUser, lastReply });
      if (!kept.length) return [];
      return [
        {
          ...message,
          widget: {
            ...message.widget!,
            customContent: (
              <RainFollowUpCard {...card.props} suggestions={kept} />
            ),
          },
        },
      ];
    }
    if (!message.widget && message.content?.trim()) {
      if (message.direction === EMessageDirection.OUTGOING)
        lastUser = message.content;
      else if (message.type !== EMessageType.THINKING)
        lastReply = message.content;
    }
    return [message];
  });
}

const RESTATEMENT_OVERLAP = 0.6;

/** Whether `later` says again most of what `earlier` said, word for word. */
function restates(earlier: string, later: string): boolean {
  const words = (text: string) =>
    new Set(text.toLowerCase().match(/[\p{L}\p{N}][\p{L}\p{N},.$€₹]{3,}/gu));
  const before = words(earlier);
  if (before.size === 0) return false;
  const after = words(later);
  let shared = 0;
  for (const word of before) if (after.has(word)) shared++;
  return shared / before.size >= RESTATEMENT_OVERLAP;
}

/**
 * A turn's text as one reply: every piece of agent text in a finished turn
 * joined, in order, into its last text message. For agents that write part of
 * an answer, look something up and then finish it, where hiding the earlier
 * text as narration would cut the answer in half. When the last piece repeats
 * most of an earlier one, the agent restated its answer after one more
 * lookup, and only the last is kept. Thinking is left out, and
 * the current turn's text stays hidden until the agent stops working, as
 * narration does.
 *
 * @param messages - The transcript, oldest first.
 * @param working - Whether the agent is still working on the last turn.
 * @returns The transcript with each finished turn's text merged.
 */
export function mergeTurnText(
  messages: IChatMessage[],
  working: boolean,
): IChatMessage[] {
  const out: (IChatMessage | null)[] = [];
  let turn: number[] = [];
  const flush = () => {
    if (turn.length > 1) {
      const last = turn[turn.length - 1];
      const pieces = turn.map(i => out[i]!.content!.trim());
      const final = pieces[pieces.length - 1];
      out[last] = {
        ...out[last]!,
        // A last piece that says the earlier ones again replaces them.
        content: pieces.slice(0, -1).some(piece => restates(piece, final))
          ? final
          : pieces.join('\n\n'),
      };
      for (const i of turn.slice(0, -1)) out[i] = null;
    }
    turn = [];
  };
  for (const message of messages) {
    // Thinking is the agent's working, not its reply.
    if (message.type === EMessageType.THINKING) continue;
    // An error (a lost connection) stands on its own, never inside an answer.
    if (
      message.direction === EMessageDirection.OUTGOING ||
      message.status === EMessageStatus.ERROR
    )
      flush();
    else if (!message.widget && message.content?.trim()) turn.push(out.length);
    out.push(message);
  }
  if (working) for (const i of turn) out[i] = null;
  else flush();
  return out.filter((message): message is IChatMessage => message !== null);
}

/**
 * The return leg of a handoff draws nothing, so it is dropped before folding
 * rather than left to split a run of work around an empty row.
 */
const isEmptyTransferMarker = (message: IChatMessage): boolean =>
  message.id.startsWith(TRANSFER_MARKER_ID_PREFIX) &&
  message.widget?.customContent == null;

/**
 * A tool call the agent loop is PAUSED on, waiting for the user to approve or
 * deny it.
 *
 * Such a call must never be hidden. Its card carries the only Approve/Deny
 * buttons there are, so removing it strands the agent on a decision the user
 * can no longer see, let alone make — and nothing in the transcript would say
 * why the thread went quiet. `buildToolCallMessage` already refuses to demote
 * one of these to a copy-only row for exactly this reason.
 *
 * A call whose decision is already RECORDED is ordinary history and hides
 * normally; only a live prompt is exempt.
 */
const isAwaitingApprovalMessage = (message: IChatMessage): boolean => {
  const content = message.widget?.customContent;
  if (!isValidElement(content)) return false;
  return (
    (content as ReactElement<IRainToolCallCardProps>).props.awaitingApproval ===
    true
  );
};

/**
 * A one-click reply offered above the composer.
 *
 * It SENDS A MESSAGE — it never performs the action itself. That distinction is
 * the whole safety story: "handle all six at once" reaches the agent as a
 * request, so the agent's own gates (and its per-exception tool bindings) still
 * apply, rather than a chip in the UI quietly mutating six runs.
 */
export interface IRainQuickReply {
  id: string;
  /** What the operator reads. Short — these sit on one line where they can. */
  label: string;
  /** What is actually sent, which can be fuller than the label. */
  message: string;
  /**
   * Offer this only once the agent has said something. For replies that answer a
   * proposal ("take that approach"), which are meaningless before there is one.
   */
  requiresAgentMessage?: boolean;
  /** Only offer this starter before the operator sends their first message. */
  initialOnly?: boolean;
}

export interface IRainChatViewProps {
  chat: IUseRainThreadMessagesReturn;
  attachments: IUseRainChatAttachmentsReturn;
  preview: IUseRainArtifactPreviewReturn;
  /** Node rendered between the chat scroll area and the input (e.g. inline Todo). */
  belowChatSlot?: ReactNode;
  /**
   * Node rendered INSIDE the chat scroll, after the messages (and the
   * awaiting-response spinner). Used to mirror the astral run assistant, which
   * appends outputs / a "resolve" affordance as the last scrolled item above
   * the input.
   */
  afterMessages?: ReactNode;
  /**
   * Message rendered as the first incoming message, above the streamed thread
   * messages (e.g. the exception description on the run-page guidance overlay).
   * Mirrors the astral `QuestionCard.initialMessage` so the rain guidance chat
   * leads with the exception the user is being asked to resolve.
   */
  initialMessage?: string;
  /**
   * Strips a leading `<exception>…</exception>` payload from displayed messages,
   * dropping the message entirely when the payload was all of it.
   *
   * That payload is the agent's INPUT — a traceback, byte offsets and an error
   * class — not a message to an operator, and surfaces that set this already say
   * what stopped in business terms. It arrives in two shapes: alone, when the
   * Astral service posts it on the cluster, and fused to the operator's first
   * message on the local path, where the kickoff rides out with whatever they
   * typed. Both are handled.
   */
  hideExceptionMessages?: boolean;
  /**
   * Rewrites each displayed message's text after any `<exception>` stripping.
   * Returning null hides the message; the agent still received the original.
   */
  displayMessage?: (content: string) => string | null;
  /**
   * Strips a leading `<tag>…</tag>` context block from displayed messages, the
   * same way {@link hideExceptionMessages} strips an exception payload. For
   * surfaces that brief the agent in the first message (what the user is
   * looking at) without showing the briefing.
   */
  hiddenContextTag?: string;
  className?: string;
  /** Compact typography and composer for narrow agent-building panels. */
  compact?: boolean;
  /** Spacing merged over the composer's own, for surfaces that pad it away from the pane edge. */
  composerClassName?: string;
  /** Receives the composer's textarea, so a surface can focus it from a shortcut. */
  composerRef?: Ref<HTMLTextAreaElement>;
  /**
   * Spacing on the scrolling message list itself (not the surface around it).
   * Set by surfaces whose chat sits under an overlapping header — the draft
   * editor pads the list's top so the first message clears the fade strip.
   */
  messageListClassName?: string;
  emptyStateDescription?: string;
  emptyStateTitle?: string;
  emptyStateIcon?: IconType;
  /**
   * Renders the attachment `<PreviewLayover>` (default true). Set false when the
   * same `preview` bundle drives multiple `RainChatView`s sharing one stream
   * (run page), so a single layover is rendered by the parent instead of one
   * per view.
   */
  showPreviewLayover?: boolean;
  /**
   * Scope id under which the unsent composer text is persisted (the single
   * editor draft slot), so it survives navigating away and back. Set only by
   * the editor (the Rain thread id); omitted by non-persistent surfaces
   * (run-page assistant + guidance overlay), which then keep ephemeral
   * in-memory composer state.
   */
  draftScopeId?: string;
  /**
   * Blocks the composer on top of the usual busy states. Set by surfaces whose
   * conversation can be read-only for reasons the chat itself can't see — the
   * finance investigator chat disables it on closed/archived case files.
   */
  composerDisabled?: boolean;
  /** Surface-owned references displayed inside the input without altering its draft. */
  composerChips?: IChatInputChip[];
  onComposerChipRemove?: (id: string) => void;
  /**
   * Composer placeholder. Defaults to the generic "Type here…". Surfaces that
   * render two chats side by side (the agent builder: the builder conversation
   * next to the agent test sandbox) MUST set distinct text, otherwise the two
   * composers are indistinguishable and it's easy to type into the wrong one.
   */
  composerPlaceholder?: string;
  /**
   * Opening line offered above an untouched composer and accepted with Tab.
   * For surfaces that know how the conversation should start but should not
   * start it unasked — the operator still chooses to send, and can edit first.
   */
  composerSuggestion?: string;
  /** Replaces the composer and its quick replies, for surfaces that offer one action instead of free text; `null` shows neither. */
  composerSlot?: ReactElement | null;
  /** Surface-owned boundary immediately before a message. */
  beforeMessage?: (message: IChatMessage) => ReactNode;
  /**
   * One-click replies shown above an untouched composer. Each sends a message;
   * none acts directly — see `IRainQuickReply`.
   */
  quickReplies?: IRainQuickReply[];
  /**
   * Overrides the thinking-message copy. Rarely needed.
   *
   * DEFAULTS TO PLAIN WORDS, deliberately. Left to the design system this reads
   * "Neurosymbolizing…" / "Neurosymbolized for Xs" — the Kognitos-internal name
   * for the architecture, shown to a finance operator who is here to find out why
   * a bill did not post. Seven surfaces passed `useThinkingTexts()` to opt out of
   * that and four forgot, which is the wrong way round: the jargon should be the
   * thing you opt IN to, and nobody has. Pass this only to say something more
   * specific than "Thinking…".
   */
  thinkingTexts?: IChatThinkingTexts;
  /**
   * Renders a "Selected" snippet as a composer chip label. The runtime shows the
   * raw text; a surface whose snippets mean something to it — the editor's
   * manifest steps — formats them instead.
   */
  formatSnippetLabel?: (snippetText: string) => string;
  /**
   * Offers the full-transcript toggle above the messages, defaulting to ON.
   *
   * Set by surfaces whose agent config is `toolVerbosity: 'full'` — the agent
   * builder and its test sandbox. The two halves are deliberately separate: the
   * config decides what the RUNTIME produces (which calls get state and cards),
   * and this decides what the VIEW folds. Flipping the config at runtime instead
   * would change `isVisibleTool`'s identity and remount every card in the
   * transcript, so the toggle would clear the thread it was meant to reveal.
   */
  offerFullContext?: boolean;
  /**
   * Whether the offered full transcript starts shown. A surface whose users
   * read answers rather than audit the agent starts with it hidden.
   */
  defaultShowToolCalls?: boolean;
  /**
   * With tool calls hidden, what happens to agent text written between them:
   * `hide` drops it as narration; `merge` joins a turn's text into one reply
   * (see {@link mergeTurnText}).
   */
  narration?: 'hide' | 'merge';
  /**
   * Trims follow-up suggestion chips before they show, given the user's last
   * message and the agent's last reply (e.g. to drop a chip asking for what
   * was just done). A card left with no chips is not shown.
   */
  filterFollowUps?: (
    suggestions: string[],
    recent: { lastUser: string; lastReply: string },
  ) => string[];
  'data-testid'?: string;
}

/**
 * Presentational Rain chat surface: the lattice `<Chat>` message stream, the
 * `<ChatInput>` composer, the awaiting-response spinner, and the attachment
 * `<PreviewLayover>`. Scope-agnostic — every chat surface (editor `RainChatBody`,
 * run-page assistant + guidance overlay) feeds it the same `{ chat, attachments,
 * preview }` bundles so the SSE stream and message list are shared while each
 * surface manages its own composer draft.
 */
export function RainChatView({
  chat,
  attachments,
  preview,
  belowChatSlot,
  afterMessages,
  initialMessage,
  hideExceptionMessages = false,
  displayMessage,
  hiddenContextTag,
  className,
  compact = false,
  composerClassName,
  composerRef,
  messageListClassName,
  emptyStateDescription,
  emptyStateTitle,
  emptyStateIcon,
  showPreviewLayover = true,
  draftScopeId,
  composerDisabled = false,
  composerChips,
  onComposerChipRemove,
  composerPlaceholder,
  composerSuggestion,
  composerSlot,
  beforeMessage,
  offerFullContext = false,
  defaultShowToolCalls = true,
  narration = 'hide',
  filterFollowUps,
  quickReplies,
  thinkingTexts,
  formatSnippetLabel,
  'data-testid': dataTestId = 'rain-chat-body',
}: IRainChatViewProps) {
  const { t } = useTranslation('rain');
  // Plain wording unless a caller asks for something more specific.
  const defaultThinkingTexts = useThinkingTexts();
  const resolvedThinkingTexts = thinkingTexts ?? defaultThinkingTexts;
  const [inputValue, setInputValue] = usePersistedDraftPrompt(draftScopeId);
  // "Select"-ed preview snippets shown as removable chips above the composer;
  // folded into the message on send.
  const {
    snippets: selectionSnippets,
    removeSnippet: removeSelectionSnippet,
    clear: clearSelectionContext,
    addSnippet: addSelectionSnippet,
  } = useSelectionContext(draftScopeId);
  const chips = useMemo<IChatInputChip[]>(
    () =>
      selectionSnippets.map(snippet => ({
        id: snippet.id,
        label: formatSnippetLabel?.(snippet.text) ?? snippet.text,
      })),
    [selectionSnippets, formatSnippetLabel],
  );
  // Disables the stop button the instant it's clicked; released once the stop
  // request settles (`stopGeneration` handles its own errors and never throws).
  const [isStopping, setIsStopping] = useState(false);
  /*
   * Work runs the operator has explicitly opened or closed, by run id. Only
   * their choices live here — a run they never touched follows the default
   * ("open while live"), so it closes itself once the work finishes without
   * needing an entry. Held in the view rather than in the run's own header
   * because opening a run puts its rows back into this list.
   */
  const [expandedRuns, setExpandedRuns] = useState<
    ReadonlyMap<string, boolean>
  >(new Map());
  const handleRunExpandedChange = useCallback(
    (runId: string, expanded: boolean) =>
      setExpandedRuns(previous => new Map(previous).set(runId, expanded)),
    [],
  );
  /*
   * Whether the full transcript is shown. Defaults to the offer, so a surface
   * that opts in opens showing everything and the operator can quiet it down —
   * rather than having to discover the toggle to see what their agent did.
   */
  const [showToolCalls, setShowToolCalls] = useState(defaultShowToolCalls);

  const {
    messages,
    sendMessageToThread,
    isSending,
    isGenerating,
    isLoadingHistory,
    stopGeneration,
    renderWorkBlock,
  } = chat;

  /*
   * Smoothed for the transcript only: the raw flag dips once per cycle of a
   * tool loop, and the work block would blink in step with it. The composer
   * keeps the raw flag, so Stop tracks the turn exactly.
   */
  const isWorking = useSustainedFlag(isSending || isGenerating);

  const {
    latticeAttachments,
    handleFilesAdd,
    handleAttachmentRemove,
    clearAttachments,
    restoreAttachments,
    buildMessagePayload,
    isUploading,
    hasFailedUploads,
    attachments: stagedAttachments,
  } = attachments;

  /*
   * The suggested opening is an offer, and an offer is spent once taken. It used
   * to reappear the moment the composer went empty again — including right after
   * the operator accepted it and pressed enter, so a thread that was already under
   * way still nagged with its own first line. Any outgoing message means they have
   * spoken, and the composer goes back to its ordinary placeholder.
   */
  const hasOperatorSpoken = messages.some(
    message => message.direction === EMessageDirection.OUTGOING,
  );
  const offerSuggestion =
    !!composerSuggestion && !inputValue && !hasOperatorSpoken;

  /*
   * Shown only over an EMPTY composer and only when the agent is not mid-turn:
   * a one-click reply that lands halfway through a generation reads as an
   * interruption, and one sitting under half-typed text competes with it.
   */
  const agentHasSpoken = messages.some(
    message => message.direction === EMessageDirection.INCOMING,
  );
  const offeredReplies = (quickReplies ?? []).filter(
    reply =>
      (!reply.requiresAgentMessage || agentHasSpoken) &&
      (!reply.initialOnly || !hasOperatorSpoken),
  );
  const showQuickReplies =
    offeredReplies.length > 0 &&
    !inputValue &&
    !isGenerating &&
    !isSending &&
    !composerDisabled;

  const handleStop = useCallback(async () => {
    setIsStopping(true);
    try {
      await stopGeneration();
    } finally {
      setIsStopping(false);
    }
  }, [stopGeneration]);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (composerDisabled || isUploading || isSending || isGenerating) return;
      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) handleFilesAdd(files);
    },
    [composerDisabled, handleFilesAdd, isGenerating, isSending, isUploading],
  );

  const handleSend = useCallback(
    async (text: string) => {
      if (composerDisabled) return;
      if (isUploading || hasFailedUploads) {
        toast.error(t('rain.error.attachmentsNotReady'));
        return;
      }
      // Fold any "Selected" preview snippets into the message as quoted context.
      const draft = buildMessagePayload(
        foldSelectionSnippets(text, selectionSnippets),
      );
      const hasText = !!draft.content;
      const hasAttachments = (draft.attachments?.length ?? 0) > 0;
      if (!hasText && !hasAttachments) return;
      // sendMessageToThread renders the message optimistically, so clear the
      // composer immediately — otherwise the text and attachments show twice
      // until the API resolves. Snapshot first so a failed send fully restores.
      const attachmentSnapshot = stagedAttachments;
      const snippetSnapshot = selectionSnippets;
      setInputValue('');
      clearAttachments();
      clearSelectionContext();
      const ok = await sendMessageToThread(draft);
      if (!ok) {
        setInputValue(text);
        restoreAttachments(attachmentSnapshot);
        // Anchor included, so a restored chip keeps its highlight in the
        // document it was selected from.
        snippetSnapshot.forEach(s => addSelectionSnippet(s.text, s.anchor));
      }
    },
    [
      addSelectionSnippet,
      buildMessagePayload,
      clearAttachments,
      clearSelectionContext,
      composerDisabled,
      hasFailedUploads,
      isUploading,
      restoreAttachments,
      selectionSnippets,
      sendMessageToThread,
      setInputValue,
      stagedAttachments,
      t,
    ],
  );

  // Prepend the exception description as the first incoming message when
  // provided (run-page guidance overlay), matching the astral QuestionCard.
  // Kept separate from `messages` so the awaiting-response logic below still
  // reads the real last streamed message.
  const displayMessages = useMemo<IChatMessage[]>(() => {
    const hidden = [
      ...(hideExceptionMessages ? ['exception'] : []),
      ...(hiddenContextTag ? [hiddenContextTag] : []),
    ];
    const unwrapped = hidden.length
      ? messages.flatMap(message => {
          /*
           * STRIP the payload, do not require it to be the whole message.
           *
           * This used to drop a message only when it both started AND ended
           * with the tags, which is true on the cluster path where the Astral
           * service posts the payload alone. On the local path the kickoff
           * rides out fused to the operator's FIRST message —
           * `${kickoff}\n\n${draft.content}` — so it starts with `<exception>`
           * and ends with whatever the operator typed. Neither half of the
           * predicate held, nothing was hidden, and the Workbench opened with a
           * raw traceback, byte offsets and an error class in front of a finance
           * operator — with the sentence they actually sent buried underneath it.
           *
           * Stripping handles both shapes: payload alone leaves nothing and the
           * message goes; payload plus a question leaves the question.
           */
          const content = message.content ?? '';
          const stripped = hidden
            .reduce(
              (text, tag) =>
                text.replace(
                  new RegExp(`^\\s*<${tag}>[\\s\\S]*?</${tag}>\\s*`),
                  '',
                ),
              content,
            )
            .trim();
          // Nothing stripped: hand the message back untouched, so a message
          // that legitimately has no text (attachments only) is not disturbed.
          if (stripped === content.trim()) return [message];
          // The payload WAS the whole message, so there is nothing left to show.
          if (!stripped) return [];
          return [{ ...message, content: stripped }];
        })
      : messages;
    const drawn = unwrapped.filter(message => !isEmptyTransferMarker(message));
    const presented = chat.filterUserMessages
      ? drawn.flatMap(message => {
          if (message.widget || message.status === EMessageStatus.ERROR)
            return [message];
          if (
            message.direction === EMessageDirection.INCOMING &&
            message.type === EMessageType.THINKING
          )
            return [];
          if (!isAssistantText(message)) return [message];
          const content = readUserMessages(message.content ?? '').text;
          return content ? [{ ...message, content }] : [];
        })
      : drawn;
    const visible = displayMessage
      ? presented.flatMap(message => {
          const content = message.content ?? '';
          const shown = displayMessage(content);
          if (shown === null) return [];
          return shown === content
            ? [message]
            : [{ ...message, content: shown }];
        })
      : presented;
    /*
     * Fold each stretch of agent work into one collapsible row. Applied here,
     * over the list about to be rendered, so it covers replayed history and
     * live streaming identically and never rewrites what the stream produced.
     *
     * Skipped wherever full context is OFFERED — not merely wherever the toggle
     * is on. A header reading "Worked for 12s" is the summary these surfaces
     * exist to replace, so folding is wrong for them in either toggle state.
     */
    const trimmed = filterFollowUps
      ? trimFollowUps(visible, filterFollowUps)
      : visible;
    const merged =
      !chat.filterUserMessages && !showToolCalls && narration === 'merge'
        ? mergeTurnText(trimmed, isWorking)
        : trimmed;
    const hiddenNarration =
      chat.filterUserMessages || showToolCalls || narration !== 'hide'
        ? undefined
        : interimNarrationIds(trimmed, isWorking);
    const filtered = showToolCalls
      ? trimmed
      : merged.filter(
          message =>
            !hiddenNarration?.has(message.id) &&
            (!isGenericToolCardMessage(message) ||
              isAwaitingApprovalMessage(message)),
        );
    const listed = offerFullContext
      ? filtered
      : foldWorkRuns(filtered, {
          isGenerating: isWorking,
          expanded: expandedRuns,
          onExpandedChange: handleRunExpandedChange,
          renderWorkBlock,
        });

    if (!initialMessage) return listed;
    const initialChatMessage: IChatMessage = {
      id: 'initial-message',
      content: initialMessage,
      direction: EMessageDirection.INCOMING,
    };
    return [initialChatMessage, ...listed];
  }, [
    chat.filterUserMessages,
    expandedRuns,
    handleRunExpandedChange,
    displayMessage,
    filterFollowUps,
    hideExceptionMessages,
    hiddenContextTag,
    initialMessage,
    isWorking,
    messages,
    narration,
    offerFullContext,
    renderWorkBlock,
    showToolCalls,
  ]);

  const renderMessage = useCallback(
    (message: IChatMessage): ReactNode => {
      const customContent = message.widget?.customContent;

      // Render the shared tool-call activity at the
      // same level so both variants have identical outer width and alignment.
      //
      // TWO CHECKS, for two kinds of card. The generic tool card is named, as it
      // always has been -- it lives in the core beside this view, so naming it
      // costs nothing. An agent's own card cannot be named here: profiles depend
      // on the core, never the reverse, so those opt in with
      // `RAIN_FULL_WIDTH_CARD` instead -- see `full-width-card.ts`.
      if (
        isFullWidthCardElement(customContent) ||
        (isValidElement(customContent) &&
          customContent.type === RainToolCallCard)
      ) {
        return (
          <div
            className="w-full [&:not(:last-child)]:-mb-3"
            data-slot="rain-tool-call-message"
          >
            {customContent}
          </div>
        );
      }

      return (
        <>
          {beforeMessage?.(message)}
          <ChatMessage message={message} />
        </>
      );
    },
    [beforeMessage],
  );

  // Gated like lattice's own `self-end`, so the empty state stays centred.
  const anchorTop = displayMessages.length > 0 || isLoadingHistory;

  return (
    <Flex
      direction="column"
      className={cn(
        'relative flex-1 min-h-0 min-w-0 w-full',
        compact &&
          '[&_p]:text-sm [&_p]:leading-[1.5] [&_li]:text-sm [&_li]:leading-[1.5] [&_ol]:pl-4 [&_ul]:pl-4',
        className,
      )}
      data-testid={dataTestId}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {offerFullContext && (
        <RainToolCallsToggle
          showToolCalls={showToolCalls}
          onShowToolCallsChange={setShowToolCalls}
        />
      )}
      <Chat
        showTimestamp
        showGradient={false}
        messages={displayMessages}
        renderMessage={renderMessage}
        emptyStateTitle={emptyStateTitle}
        emptyStateIcon={emptyStateIcon}
        emptyStateDescription={
          emptyStateDescription ?? t('rain.emptyState.description')
        }
        // Targets a data-testid because lattice's messages wrapper has no data-slot.
        // Every Rain thread top-anchors; `self-start` stands in for `contentAlign` (kognitos/lattice#167).
        chatContainerClassName={cn(
          'min-w-0 pb-3 px-3 [&_[data-slot=chat-thinking-message]]:w-full [&_[data-slot=chat-thinking-message]>*]:w-full [&_[data-testid=chat-thinking-messages]]:w-full [&_[data-slot=chat-thinking-message]>[role=button]]:justify-start [&_[data-slot=chat-thinking-message]>[role=button]>:first-child]:w-auto [&_[data-slot=chat-thinking-message]>[role=button]>:first-child]:flex-none [&_[data-slot=chat-activity]]:w-full [&_[data-slot=chat-activity]>*]:w-full',
          anchorTop && 'self-start',
          messageListClassName,
        )}
        isLoading={isLoadingHistory}
        thinkingTexts={resolvedThinkingTexts}
      >
        {afterMessages}
      </Chat>
      {belowChatSlot}
      {composerSlot !== undefined ? (
        composerSlot
      ) : (
        <>
          {showQuickReplies && (
            <Flex gap={1.5} wrap="wrap" className="px-1 pt-2">
              {offeredReplies.map(reply => (
                <Button
                  key={reply.id}
                  type="button"
                  variant="outline"
                  size="xs"
                  className="h-auto rounded-full px-3 py-1 font-normal"
                  onClick={() => void handleSend(reply.message)}
                  data-testid={`rain-quick-reply-${reply.id}`}
                >
                  {reply.label}
                </Button>
              ))}
            </Flex>
          )}
          <ChatInput
            ref={composerRef}
            className={cn('-m-1 px-1 pb-2 pt-3', composerClassName)}
            chips={[...(composerChips ?? []), ...chips]}
            onChipRemove={id => {
              if (composerChips?.some(chip => chip.id === id))
                onComposerChipRemove?.(id);
              else removeSelectionSnippet(id);
            }}
            value={inputValue}
            /*
             * While the composer is untouched the SUGGESTION is the placeholder,
             * rather than sitting above a second "Message Astral…" prompt — two
             * pieces of grey text competing to say what to type read as clutter,
             * and the one that matters is the one you can accept.
             */
            placeholder={
              offerSuggestion ? composerSuggestion : composerPlaceholder
            }
            /*
             * Reaches the textarea through the wrapper: lattice 1.50 renders
             * `inputClassName` on an `input-group` div, and `placeholder:` only
             * styles the element that owns the placeholder, so putting the variant
             * on the wrapper silently styles nothing.
             */
            inputClassName={
              cn(
                offerSuggestion && '[&>textarea]:placeholder:italic',
                compact &&
                  '[&>textarea]:min-h-12 [&>textarea]:text-sm [&>textarea]:py-2',
              ) || undefined
            }
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={event => {
              // Tab on an untouched composer accepts the suggestion rather than
              // moving focus. Only when there is something to accept, so Tab keeps
              // its normal meaning everywhere else.
              if (event.key === 'Tab' && !event.shiftKey && offerSuggestion) {
                event.preventDefault();
                setInputValue(composerSuggestion);
              }
            }}
            onSend={text => void handleSend(text)}
            isLoading={isSending || isGenerating}
            loadingMode={isSending ? 'loader' : 'stop'}
            tooltips={{
              stop: {
                content: t('rain.action.stopResponse'),
                variant: 'primary',
              },
            }}
            onStop={isSending ? undefined : handleStop}
            isStopping={isStopping}
            attachments={latticeAttachments}
            onAddAttachments={handleFilesAdd}
            onAttachmentRemove={handleAttachmentRemove}
            allowFilePaste
            disabled={
              composerDisabled ||
              isUploading ||
              isSending ||
              isGenerating ||
              isLoadingHistory
            }
          />
        </>
      )}
      {showPreviewLayover && (
        <PreviewLayover
          isOpen={
            preview.previewFile !== null || preview.previewLoadingName !== null
          }
          isLoading={preview.previewLoadingName !== null}
          loadingLabel={preview.previewLoadingName ?? undefined}
          outputName=""
          outputValue={preview.previewFile}
          onClose={preview.closePreview}
          onDownload={downloadOutput}
        />
      )}
    </Flex>
  );
}
